import {useEffect, useRef} from 'react';
import type {ReactNode} from 'react';
import type {
  SourceEntityRequest,
  TaskDefinitionResponse,
  TaskDefinitionRequest,
  TaskDefinitionStateEnum,
} from 'src/api/ProactApiClient';
import type {DueDatePreviewFormProps} from 'src/app/forms/DueDatePreviewForm';
import type {ProactFormApi} from 'src/app/forms/engine/useProactForm';
import type {IFormDataV2, IFormFieldV2} from 'src/types/FormV2';
import {toFormDataV2} from 'src/app/forms/engine/formData';

export interface TaskFeatureFormOptions {
  taskState?: TaskDefinitionStateEnum;
  initialData?: IFormDataV2;
  resetKey?: string | number;
  transformMeta?: (fields: IFormFieldV2[]) => IFormFieldV2[];
}

export interface TaskFeatureFormContext {
  formApi: ProactFormApi;
  isLoading: boolean;
  form: ReactNode;
  getFeatureFields: () => TaskDefinitionRequest;
}

export interface TaskFeatureEditorProps extends TaskFeatureFormOptions {
  children: (context: TaskFeatureFormContext) => ReactNode;
  onValuesChange?: DueDatePreviewFormProps['onValuesChange'];
}

export const mapComplianceStandardIds = (
  values: IFormDataV2
): number[] | undefined =>
  Array.isArray(values.complianceStandardIds)
    ? values.complianceStandardIds.map((option: {id: number}) => option.id)
    : undefined;

export const toTaskDefinitionFormData = (
  item: TaskDefinitionResponse
): IFormDataV2 =>
  toFormDataV2({
    ...item,
    previewDueAt: item.dueInDays ? item.dueAt : undefined,
    inlineAttachments: item.metadata?.inlineAttachments,
    sourceEntities: {
      selectAllEntitiesType: item.metadata?.selectAllEntityType,
      entities: item.sourceEntities.map(entity => ({
        entityId: entity.entityId,
        entityType: entity.entityType,
      })),
    } as SourceEntityRequest,
  });

export const useResetTaskFeatureForm = (
  formApi: ProactFormApi,
  initialData: IFormDataV2 | undefined,
  resetKey: string | number | undefined
): void => {
  const appliedResetKey = useRef<string | number>();

  useEffect(() => {
    if (
      resetKey === undefined ||
      !initialData ||
      appliedResetKey.current === resetKey
    ) {
      return;
    }
    formApi.resetForm(initialData);
    appliedResetKey.current = resetKey;
  }, [formApi, initialData, resetKey]);
};
